import clsx from "clsx";
import {
  useTitleSectionsStore,
  type SectionId,
} from "../../../../../journal-title-sections";
import { ChatMessage } from "../../../../model";
import { useTypewriterText } from "./lib/use-typewriter-text";
import "./ai-chat-message.scss";

interface AiChatMessageProps {
  message: ChatMessage;
}

export const AiChatMessage = ({ message }: AiChatMessageProps) => {
  const {
    badge,
    title,
    description,
    typing,
    commentLabel,
    commentText,
    errors,
    statusIcon,
    personCard,
  } = message;
  const openSections = useTitleSectionsStore((state) => state.openSections);

  const shouldAnimate = !typing;
  const titleStr = typeof title === "string" ? title : undefined;
  const descStr = typeof description === "string" ? description : undefined;
  const commentLabelStr =
    typeof commentLabel === "string" ? commentLabel : undefined;
  const commentTextStr =
    typeof commentText === "string" ? commentText : undefined;

  const { text: animatedTitle, done: titleDone } = useTypewriterText(
    titleStr,
    shouldAnimate,
  );
  const { text: animatedDesc, done: descDone } = useTypewriterText(
    descStr,
    shouldAnimate,
  );
  const { text: animatedCommentLabel } = useTypewriterText(
    commentLabelStr,
    shouldAnimate,
  );
  const { text: animatedCommentText } = useTypewriterText(
    commentTextStr,
    shouldAnimate,
  );

  const showErrors = Boolean(
    errors && errors.length > 0 && (typing || (titleDone && descDone)),
  );
  const showCommentLabel = Boolean(
    commentLabelStr ? animatedCommentLabel : commentLabel,
  );
  const showCommentText = Boolean(
    commentTextStr ? animatedCommentText : commentText,
  );

  const handleErrorClick = (sectionId?: string) => {
    if (sectionId) {
      openSections([sectionId as SectionId]);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  return (
    <div
      className={clsx("ai-chat-message", statusIcon === "rejected" && "ai-chat-message--rejected")}
    >
      {(statusIcon === "rejected" || statusIcon === "warning") && title ? (
        <div className="ai-chat-message__status-header">
          <div
            className={clsx("ai-chat-message__status-icon", "ai-chat-message__status-icon--", statusIcon)}
          />
          <div className="ai-chat-message__title ai-chat-message__title--status">
            {titleStr ? animatedTitle : title}
          </div>
        </div>
      ) : null}
      {badge && statusIcon !== "rejected" && (
        <div
          className={clsx("ai-chat-message__badge", "ai-chat-message__badge--", badge.color)}
        >
          {badge.text}
        </div>
      )}
      {title && statusIcon !== "rejected" && statusIcon !== "warning" && (
        <div className="ai-chat-message__title">
          {titleStr ? animatedTitle : title}
        </div>
      )}
      {description && (
        <div className="ai-chat-message__description">
          {descStr ? animatedDesc : description}
        </div>
      )}
      {(showCommentLabel || statusIcon === "rejected") && (
        <div className="ai-chat-message__comment-label">
          {commentLabelStr ? animatedCommentLabel : commentLabel}
        </div>
      )}
      {showErrors && (
        <ul className="ai-chat-message__errors-list">
          {errors?.map((error) => (
            <li key={error.id} className="ai-chat-message__error-item">
              <button
                type="button"
                className="ai-chat-message__error-item-btn"
                onClick={() => handleErrorClick(error.sectionId)}
              >
                {error.buttonLabel ?? (typeof error.description === "string"
                  ? error.description
                  : error.description)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {(showCommentText || statusIcon === "rejected") && (
        <div className="ai-chat-message__comment-text">
          {commentTextStr ? animatedCommentText : commentText}
        </div>
      )}
      {personCard ? (
        <div className="ai-chat-message__person-card">
          <div className="ai-chat-message__person-title">
            {personCard.label}{" "}
            <span className="ai-chat-message__person-name">
              {personCard.name}
            </span>
          </div>
          {personCard.description ? (
            <div className="ai-chat-message__person-description">
              {personCard.description}
            </div>
          ) : null}
        </div>
      ) : null}
      {typing && (
        <div className="ai-chat-message__typing" aria-label="typing">
          <span className="ai-chat-message__typing-dot" />
          <span className="ai-chat-message__typing-dot" />
          <span className="ai-chat-message__typing-dot" />
        </div>
      )}
    </div>
  );
};
